import { mainAdminSplitApi } from 'redux/store/mainSplitApi'
import { getSimpleGrade, type QueryParameters, type QueryResult } from 'types'
import { type Rasn, type RasnDetails, type RasnEvent, type RasnEventType } from 'types/RASN'
import { loadToken } from 'utils/OktaToken'

const rasnApi = mainAdminSplitApi.injectEndpoints({
  endpoints: (builder) => ({
    // Partial allows the query parameters to be optional, in which case they
    // are omitted from the request URI (which the backend knows how to handle)
    searchRasns: builder.query<QueryResult<Rasn>, Partial<QueryParameters<Rasn>>>({
      query: (parameters) => {
        return {
          url: '/rasns',
          params: parameters
        }
      },
      providesTags: ['SearchRasns']
    }),
    getRasn: builder.query<RasnDetails, string>({
      query: (rasnNumber) => {
        return {
          url: `/rasns/${rasnNumber}`
        }
      },
      transformResponse (response: RasnDetails): RasnDetails {
        return {
          ...response,
          units: response.units.map((unit) => {
            return {
              ...unit,
              simpleGrade: getSimpleGrade(unit.grade)
            }
          })
        }
      },
      providesTags: (result, error, rasnNumber) => [{ type: 'RasnDetails', id: rasnNumber }]
    }),
    performRasnAction: builder.mutation<void, { rasnNumber: string, action: string }>({
      query: ({ rasnNumber, action }) => {
        return {
          url: `/rasns/${rasnNumber}`,
          method: 'PATCH',
          params: {
            action
          }
        }
      },
      invalidatesTags: (result, error, { rasnNumber }) => [{ type: 'RasnDetails', id: rasnNumber }, { type: 'SearchRasns' }]
    }),
    retriggerRasnEvents: builder.mutation<void, { rasnNumber: string, eventType: RasnEventType }>({
      query: ({ rasnNumber, eventType }) => {
        return {
          url: `/rasns/${rasnNumber}/events/${eventType}`,
          method: 'POST'
        }
      }
    }),
    getRasnEvents: builder.query<RasnEvent[], string>({
      query: (rasnNumber) => {
        return {
          url: `/rasns/${rasnNumber}/events`
        }
      }
    })
  })
})

export const {
  useSearchRasnsQuery,
  useGetRasnQuery,
  useGetRasnEventsQuery,
  usePerformRasnActionMutation,
  useRetriggerRasnEventsMutation
} = rasnApi

export async function exportRlpnCsv (params: Record<string, string>): Promise<void> {
  const baseUrl = (process.env.REACT_APP_ADMIN_API_URL ?? '').replace(/\/$/, '')
  const token = loadToken()
  const query = new URLSearchParams(params).toString()
  const url = `${baseUrl}/rasns/rlpn/export?${query}`

  const response = await fetch(url, {
    headers: token ? { Authorization: `Bearer ${token}` } : {}
  })

  if (!response.ok) {
    const body = await response.text()
    throw new Error(extractErrorMessage(body) || `Export failed (${response.status})`)
  }

  const blob = await response.blob()
  const timestamp = Date.now()
  const link = document.createElement('a')
  link.href = window.URL.createObjectURL(blob)
  link.download = `Rlpn list ${timestamp}.csv`
  link.click()
  window.URL.revokeObjectURL(link.href)
}

function extractErrorMessage (body: string): string {
  const trimmed = body.trim()
  if (!trimmed) return ''
  try {
    const parsed = JSON.parse(trimmed)
    if (parsed && typeof parsed.message === 'string' && parsed.message.trim()) {
      return parsed.message
    }
  } catch {
  }
  return trimmed
}

